import { SignedOutError, fetchWithSession } from '@auctisync/api-client';
import { supabase } from './supabase';

/**
 * The signed-in visitor's access token, read at the moment a request needs it
 * (2026-10-11). Never kept from a render: the token is replaced hourly, so a
 * hook that held one — keyed on the visitor, as it must be (`useSession`) —
 * would send an expired token an hour later. `getSession` hands back the
 * current one, renewed first if it has run out. Null when signed out.
 */
export async function currentAccessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/** Thrown by `fetchWithAuth` when no one is signed in at the moment of the request. */
export { SignedOutError };

/**
 * `fetch` carrying the visitor's token as it is NOW — for our own services
 * (the Intelligence Engine, the copilot), which the Supabase client does not
 * call for us. Read per request, so a hook keyed on the visitor rather than
 * the session object never sends a token that has since been replaced.
 *
 * A 401 gets one refresh and one retry (move P14), by the same rule as every
 * request through the Supabase client — `fetchWithSession` in
 * `@auctisync/api-client`, where the rule lives once. Both services answer 401
 * only at their sign-in check, before any handler runs, so the retry never
 * repeats an action.
 *
 * Rejects with `SignedOutError` when signed out; callers already treat a
 * failed request as an error state.
 */
export function fetchWithAuth(input: string, init: RequestInit = {}): Promise<Response> {
  return fetchWithSession(supabase, input, init);
}
